import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  getUserReferrals,
  getReferralLink,
  getReferralSettings,
  ensureUserReferralCode
} from "../utils/referrals";
import { formatDate } from "../utils/format";
import SEO from "../components/shared/SEO";
import {
  Gift,
  Copy,
  Check,
  Users,
  Award,
  ShoppingBag,
  Package
} from "lucide-react";
import toast from "react-hot-toast";

export default function AccountReferralsPage() {
  const { user, profile } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [referralCode, setReferralCode] = useState(profile?.referralCode || "");

  useEffect(() => {
    async function init() {
      if (!user) return;
      try {
        const [settingsData] = await Promise.all([
          getReferralSettings(),
        ]);
        setSettings(settingsData);

        let code = profile?.referralCode;
        if (!code) {
          code = await ensureUserReferralCode(user, profile);
        }
        setReferralCode(code);

        const list = await getUserReferrals(user.uid);
        setReferrals(list);
      } catch (err) {
        console.error("Error loading referral data:", err);
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [user, profile]);

  const referralLink = getReferralLink(referralCode);

  const copyToClipboard = (text, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    if (type === "link") {
      setCopiedLink(true);
      toast.success("Referral link copied to clipboard! 📋");
      setTimeout(() => setCopiedLink(false), 2500);
    } else {
      setCopiedCode(true);
      toast.success("Referral code copied! 📋");
      setTimeout(() => setCopiedCode(false), 2500);
    }
  };

  // Social share links
  const shareText = `Get a special discount on authentic solar power and energy equipment at Afriport using my referral link: ${referralLink}`;
  const shareWhatsApp = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
  const shareTwitter = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
  const shareTelegram = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent("Shop with a discount on Afriport!")}`;
  const shareEmail = `mailto:?subject=${encodeURIComponent("Discount invitation to Afriport")}&body=${encodeURIComponent(shareText)}`;

  const discountBadgeText = settings?.refereeDiscount
    ? `${settings.refereeDiscount}${settings.discountType === "percentage" ? "%" : "₦"} OFF`
    : "Discount";

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh", padding: "40px 16px" }}>
      <SEO title="Refer Friends & Earn Rewards | Afriport" />
      <div className="container section-sm" style={{ maxWidth: 940, margin: "0 auto" }}>
        
        {/* Navigation Tabs between Orders and Referrals */}
        <div style={{ display: "flex", gap: 12, marginBottom: 28, borderBottom: "1px solid var(--border)", paddingBottom: 12 }}>
          <Link
            to="/account/orders"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 14,
              color: "var(--text-muted)",
              background: "transparent",
            }}
          >
            <Package size={16} /> My Orders
          </Link>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 16px",
              borderRadius: 8,
              fontWeight: 700,
              fontSize: 14,
              color: "#fff",
              background: "var(--brand)",
            }}
          >
            <Gift size={16} /> Refer & Earn
          </div>
        </div>

        {/* Hero Card */}
        <div
          className="card"
          style={{
            padding: "clamp(24px, 5vw, 40px)",
            marginBottom: 32,
            background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
            color: "#fff",
            borderRadius: 16,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
          }}
        >
          {/* Subtle decorative glow */}
          <div
            style={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(16, 185, 129, 0.25) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <div style={{ maxWidth: 620, position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(16, 185, 129, 0.2)",
                color: "#34D399",
                padding: "6px 12px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 700,
                marginBottom: 16,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              <Gift size={14} /> Afriport Referral Program
            </div>
            <h1 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(24px, 4vw, 32px)", fontWeight: 800, lineHeight: 1.25, marginBottom: 12 }}>
              Invite Friends. Give <span style={{ color: "#34D399" }}>{discountBadgeText}</span>, Get Rewarded.
            </h1>
            <p style={{ color: "#94A3B8", fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>
              {settings?.programDescription || "Share your exclusive referral link. When friends sign up and make their first purchase, they get an instant discount and you unlock exclusive platform rewards."}
            </p>

            {/* Referral Link & Code Box */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.07)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
                borderRadius: 12,
                padding: 18,
                marginBottom: 20,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Your Unique Referral Link
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#94A3B8" }}>Code:</span>
                  <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13, background: "rgba(255,255,255,0.15)", padding: "2px 8px", borderRadius: 4, color: "#F8FAFC" }}>
                    {referralCode || "…"}
                  </span>
                  <button
                    onClick={() => copyToClipboard(referralCode, "code")}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: copiedCode ? "#34D399" : "#94A3B8",
                      cursor: "pointer",
                      padding: 2,
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Copy Code"
                  >
                    {copiedCode ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  readOnly
                  value={referralLink || "Generating your link…"}
                  style={{
                    flex: 1,
                    minWidth: 220,
                    background: "rgba(0, 0, 0, 0.35)",
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    color: "#F8FAFC",
                    fontSize: 13,
                    fontFamily: "monospace",
                    outline: "none",
                  }}
                />
                <button
                  onClick={() => copyToClipboard(referralLink, "link")}
                  style={{
                    background: copiedLink ? "#10B981" : "var(--brand)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 18px",
                    fontWeight: 700,
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {copiedLink ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy Link</>}
                </button>
              </div>
            </div>

            {/* Quick Share Buttons */}
            <div>
              <p style={{ fontSize: 12, color: "#94A3B8", marginBottom: 10, fontWeight: 600 }}>Share directly via:</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <a
                  href={shareWhatsApp}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    background: "#25D366",
                    color: "#fff",
                    textDecoration: "none",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  WhatsApp
                </a>
                <a
                  href={shareTwitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    background: "#1DA1F2",
                    color: "#fff",
                    textDecoration: "none",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Twitter / X
                </a>
                <a
                  href={shareTelegram}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    background: "#0088CC",
                    color: "#fff",
                    textDecoration: "none",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Telegram
                </a>
                <a
                  href={shareEmail}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "8px 14px",
                    borderRadius: 8,
                    background: "rgba(255, 255, 255, 0.15)",
                    color: "#fff",
                    textDecoration: "none",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Email
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20, marginBottom: 32 }}>
          <div className="card" style={{ padding: 24, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(59, 130, 246, 0.12)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Users size={24} />
            </div>
            <div>
              <p style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Friends Joined</p>
              <h3 style={{ fontSize: 28, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2, color: "var(--text)" }}>
                {referrals.length}
              </h3>
            </div>
          </div>

          <div className="card" style={{ padding: 24, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(16, 185, 129, 0.12)", color: "#10B981", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ShoppingBag size={24} />
            </div>
            <div>
              <p style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Completed Orders</p>
              <h3 style={{ fontSize: 28, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2, color: "var(--text)" }}>
                {referrals.filter(r => r.status === "completed_purchase").length}
              </h3>
            </div>
          </div>

          <div className="card" style={{ padding: 24, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(245, 158, 11, 0.12)", color: "#D97706", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Award size={24} />
            </div>
            <div>
              <p style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Rewards Earned</p>
              <h3 style={{ fontSize: 28, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2, color: "var(--text)" }}>
                {referrals.length > 0 ? `${referrals.length} Active` : "—"}
              </h3>
            </div>
          </div>
        </div>

        {/* How It Works Card */}
        <div className="card" style={{ padding: 32, marginBottom: 32 }}>
          <h2 style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, marginBottom: 24 }}>
            How the Referral Program Works
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 24 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--brand-light)", color: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16 }}>
                1
              </div>
              <h4 style={{ fontWeight: 700, fontSize: 16, marginTop: 4 }}>Share Your Invite Link</h4>
              <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.5 }}>
                Copy your exclusive link or code and share it with friends, family, or business partners.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--brand-light)", color: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16 }}>
                2
              </div>
              <h4 style={{ fontWeight: 700, fontSize: 16, marginTop: 4 }}>Friend Creates Account</h4>
              <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.5 }}>
                When they register via your link, your referral code is automatically linked to their profile.
              </p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--brand-light)", color: "var(--brand)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16 }}>
                3
              </div>
              <h4 style={{ fontWeight: 700, fontSize: 16, marginTop: 4 }}>Both Get Discount Perks</h4>
              <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.5 }}>
                Your friend gets a discount on their first purchase, and you earn special rewards on your account!
              </p>
            </div>
          </div>
        </div>

        {/* Referrals History Table */}
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700 }}>
              Friends Referred ({referrals.length})
            </h3>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: "center" }}>
              <div className="loader"><div className="spinner" /></div>
            </div>
          ) : referrals.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 24px" }}>
              <Gift size={40} color="var(--text-muted)" style={{ marginBottom: 12 }} />
              <h4 style={{ fontWeight: 700, marginBottom: 6 }}>No referrals yet</h4>
              <p style={{ color: "var(--text-muted)", fontSize: 14, maxWidth: 400, margin: "0 auto 20px" }}>
                You haven't referred any friends yet. Share your link above to start earning rewards!
              </p>
              <button onClick={() => copyToClipboard(referralLink, "link")} className="btn btn-primary btn-sm">
                <Copy size={14} /> Copy Your Link
              </button>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
                    <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>Friend</th>
                    <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>Date Joined</th>
                    <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map(r => (
                    <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "16px 20px", fontWeight: 600 }}>
                        {r.refereeName || "Anonymous Friend"}
                        {r.refereeEmail && (
                          <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 400 }}>
                            {r.refereeEmail.replace(/(?<=.{2}).(?=[^@]*?@)/g, "*")}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "16px 20px", color: "var(--text-muted)", fontSize: 13 }}>
                        {formatDate(r.createdAt)}
                      </td>
                      <td style={{ padding: "16px 20px" }}>
                        <span
                          className="badge"
                          style={{
                            background: r.status === "completed_purchase" ? "rgba(16, 185, 129, 0.12)" : "rgba(59, 130, 246, 0.12)",
                            color: r.status === "completed_purchase" ? "#047857" : "#1D4ED8",
                            fontWeight: 700,
                            padding: "4px 8px",
                            borderRadius: 6,
                            fontSize: 12,
                          }}
                        >
                          {r.status === "completed_purchase" ? "Order Completed 🎉" : "Registered"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
